import {
  BuildingOfficeIcon,
  PhoneIcon,
  UserGroupIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useAgency } from '../context/AgencyContext';

export default function AdminDashboardPage() {
  const { currentAgency, businesses, loading } = useAgency();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (!currentAgency) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold text-gray-900">No Agency Selected</h3>
        <p className="mt-2 text-sm text-gray-500">
          Select an agency from the dropdown above or create a new one.
        </p>
      </div>
    );
  }

  // Calculate stats from real data
  const stats = [
    {
      name: 'Total Clients',
      value: businesses.length,
      icon: BuildingOfficeIcon,
    },
    {
      name: 'Total Calls (30d)',
      value: businesses.reduce((acc, b) => acc + (b.stats?.totalCallsThisMonth || 0), 0),
      icon: PhoneIcon,
    },
    {
      name: 'Total Leads (30d)',
      value: businesses.reduce((acc, b) => acc + (b.stats?.totalLeads || 0), 0),
      icon: UserGroupIcon,
    },
    {
      name: 'Total Appointments (30d)',
      value: businesses.reduce((acc, b) => acc + (b.stats?.totalAppointments || 0), 0),
      icon: CalendarIcon,
    },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Agency Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Overview of {currentAgency.name}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {stats.map((stat) => (
          <div
            key={stat.name}
            className="relative overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:px-6 sm:py-6"
          >
            <dt>
              <div className="absolute rounded-md bg-primary-500 p-3">
                <stat.icon className="h-6 w-6 text-white" aria-hidden="true" />
              </div>
              <p className="ml-16 truncate text-sm font-medium text-gray-500">
                {stat.name}
              </p>
            </dt>
            <dd className="ml-16 flex items-baseline">
              <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
            </dd>
          </div>
        ))}
      </div>

      {/* Recent Clients */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            Client Businesses
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            All businesses managed by this agency
          </p>
        </div>
        <div className="overflow-hidden">
          {businesses.length === 0 ? (
            <div className="text-center py-12">
              <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-semibold text-gray-900">No clients</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by adding your first client business.
              </p>
              <div className="mt-6">
                <Link
                  to="/clients/new"
                  className="inline-flex items-center rounded-md bg-primary-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
                >
                  Add Client
                </Link>
              </div>
            </div>
          ) : (
            <ul role="list" className="divide-y divide-gray-200">
              {businesses.slice(0, 5).map((business) => (
                <li key={business.id} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <BuildingOfficeIcon className="h-5 w-5 text-primary-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-900">
                          {business.name}
                        </p>
                        <p className="text-sm text-gray-500">{business.phoneNumber}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-x-4">
                      <div className="text-right text-sm">
                        <p className="text-gray-900">
                          {business.stats?.totalCallsThisMonth || 0} calls
                        </p>
                        <p className="text-gray-500">this month</p>
                      </div>
                      <Link
                        to={`/clients/${business.id}`}
                        className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                      >
                        View
                      </Link>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        {businesses.length > 5 && (
          <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
            <Link
              to="/clients"
              className="text-sm font-semibold text-primary-600 hover:text-primary-500"
            >
              View all clients &rarr;
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
